import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getUserSettingsAsync } from './asyncActions';
import { getLocalStorage, setLocalStorage } from '../../utils';

export interface SettingsState {
  activeDir: string;
  activeThemeLayout: string;
  activeTheme: string;
  activeSidebarType: string;
  activeLogoBg: string;
  activeNavbarBg: string;
  activeSidebarBg: string;
  activeSidebarPos: string;
  activeHeaderPos: string;
  activeLayout: string;
  openSettings: boolean;
  locale: string;
  userSettings: null | any;
  systemOfMeasurement: string;
  dateFormat: string;
}

const initialState: SettingsState = {
  activeDir: 'ltr',
  activeThemeLayout: 'vertical',
  activeTheme: 'light',
  activeSidebarType: 'mini-sidebar',
  activeLogoBg: 'skin6',
  activeNavbarBg: 'skin6',
  activeSidebarBg: 'skin4',
  activeSidebarPos: 'fixed',
  activeHeaderPos: 'fixed',
  activeLayout: 'full',
  openSettings: false,
  userSettings: null,
  locale: getLocalStorage('locale') ? getLocalStorage('locale') : 'en',
  systemOfMeasurement: getLocalStorage('systemOfMeasurement')
    ? getLocalStorage('systemOfMeasurement')
    : 'METRIC_SYSTEM',
  dateFormat: getLocalStorage('dateFormat') ? getLocalStorage('dateFormat') : 'DD_MM_AAAA',
};

export const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    toggleSettings: (state) => {
      state.openSettings = !state.openSettings;
    },
    setLanguage: (state, action: PayloadAction<string>) => {
      state.locale = action.payload;
      setLocalStorage('locale', action.payload);
    },
    setMeasurementType: (state, action: PayloadAction<string>) => {
      state.systemOfMeasurement = action.payload;
      setLocalStorage('systemOfMeasurement', action.payload);
    },
    setDateFormat: (state, action: PayloadAction<string>) => {
      state.dateFormat = action.payload;
      setLocalStorage('dateFormat', action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUserSettingsAsync.pending, (state) => {
        state.userSettings = null;
        state.systemOfMeasurement = '';
        state.dateFormat = '';
      })
      .addCase(getUserSettingsAsync.fulfilled, (state, action) => {
        let data = action.payload?.item;

        state.userSettings = data;

        if (data) {
          let locale = data.settingsLanguages?.code === 'SPANISH' ? 'es' : 'en';

          state.locale = locale;
          setLocalStorage('locale', locale);

          state.systemOfMeasurement = data.settingsMeasurementUnits?.code;
          setLocalStorage('systemOfMeasurement', data.settingsMeasurementUnits?.code);

          state.dateFormat = data.settingsDateFormat?.code;
          setLocalStorage('dateFormat', data.settingsDateFormat?.code);
        }
      })
      .addCase(getUserSettingsAsync.rejected, (state) => {
        state.userSettings = null;
        state.systemOfMeasurement = '';
        state.dateFormat = '';
      });
  },
});

export const { toggleSettings, setLanguage, setMeasurementType, setDateFormat } = settingsSlice.actions;

export default settingsSlice.reducer;
